import React, { useState } from 'react';
import { USER_MODULE } from '../../config';
import { useAuth } from '../../hooks/useAuth';
import ActivityManager from './ActivityManager';
import ApiKeyManager from './ApiKeyManager';
import GroupManager from './GroupManager';
import GroupSettingsManager from './GroupSettingsManager';
import LlmUsageManager from './LlmUsageManager';
import McpAuditLog from './McpAuditLog';
import RatingsManager from './RatingsManager';
import TestingManager from './TestingManager';
import BriefTestingManager from './BriefTestingManager';
import type { SearchResult } from '../../types/api';
import TocValidatorManager from './TocValidatorManager';
import UserManager from './UserManager';

interface AdminPanelProps {
  isActive: boolean;
  dataSource?: string;
  dataSourceConfig?: import('../../App').DataSourceConfigItem;
  // The model combo selected at the top of the page; the brief citation check
  // judges with its summarisation model.
  modelCombo?: string | null;
  // Opens a cited document in the app's own preview (as search results do).
  onResultClick?: (result: SearchResult) => void;
}

type AdminTab =
  | 'users'
  | 'groups'
  | 'group-settings'
  | 'ratings'
  | 'activity'
  | 'llm-usage'
  | 'mcp-audit'
  | 'api-keys'
  | 'testing'
  | 'testing-brief'
  | 'toc-validator';

const TAB_USERS: AdminTab = 'users';
const TAB_GROUPS: AdminTab = 'groups';
const TAB_GROUP_SETTINGS: AdminTab = 'group-settings';
const TAB_RATINGS: AdminTab = 'ratings';
const TAB_ACTIVITY: AdminTab = 'activity';
const TAB_LLM_USAGE: AdminTab = 'llm-usage';
const TAB_MCP_AUDIT: AdminTab = 'mcp-audit';
const TAB_API_KEYS: AdminTab = 'api-keys';
const TAB_TESTING: AdminTab = 'testing';
const TAB_TESTING_BRIEF: AdminTab = 'testing-brief';
const TAB_TOC_VALIDATOR: AdminTab = 'toc-validator';
const ACTIVE_CLASS = 'admin-tab-active';

const tabClass = (tab: AdminTab, current: AdminTab) =>
  `admin-tab ${tab === current ? ACTIVE_CLASS : ''}`;

const AdminPanel: React.FC<AdminPanelProps> = ({
  isActive,
  dataSource = '',
  dataSourceConfig,
  modelCombo = null,
  onResultClick,
}) => {
  const { user } = useAuth();
  const [tab, setTab] = useState<AdminTab>(TAB_USERS);

  if (!USER_MODULE || !isActive || !user?.is_superuser) return null;

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <h2>Administration</h2>
        <div className="admin-tabs">
          <button
            className={tabClass(tab, TAB_USERS)}
            onClick={() => setTab(TAB_USERS)}
          >
            Users
          </button>
          <button
            className={tabClass(tab, TAB_GROUPS)}
            onClick={() => setTab(TAB_GROUPS)}
          >
            Groups
          </button>
          <button
            className={tabClass(tab, TAB_GROUP_SETTINGS)}
            onClick={() => setTab(TAB_GROUP_SETTINGS)}
          >
            Group Settings
          </button>
          <button
            className={tabClass(tab, TAB_RATINGS)}
            onClick={() => setTab(TAB_RATINGS)}
          >
            Ratings
          </button>
          <button
            className={tabClass(tab, TAB_ACTIVITY)}
            onClick={() => setTab(TAB_ACTIVITY)}
          >
            Activity
          </button>
          <button
            className={tabClass(tab, TAB_LLM_USAGE)}
            onClick={() => setTab(TAB_LLM_USAGE)}
          >
            LLM Usage
          </button>
          <button
            className={tabClass(tab, TAB_MCP_AUDIT)}
            onClick={() => setTab(TAB_MCP_AUDIT)}
          >
            MCP / A2A
          </button>
          <button
            className={tabClass(tab, TAB_API_KEYS)}
            onClick={() => setTab(TAB_API_KEYS)}
          >
            API Keys
          </button>
          <button
            className={tabClass(tab, TAB_TESTING)}
            onClick={() => setTab(TAB_TESTING)}
          >
            Testing (Search + AI Summary)
          </button>
          <button
            className={tabClass(tab, TAB_TESTING_BRIEF)}
            onClick={() => setTab(TAB_TESTING_BRIEF)}
          >
            Testing (Brief)
          </button>
          <button
            className={tabClass(tab, TAB_TOC_VALIDATOR)}
            onClick={() => setTab(TAB_TOC_VALIDATOR)}
          >
            TOC Validator
          </button>
        </div>
      </div>
      <div className="admin-tab-content">
        {tab === TAB_USERS && <UserManager />}
        {tab === TAB_GROUPS && <GroupManager />}
        {tab === TAB_GROUP_SETTINGS && <GroupSettingsManager />}
        {tab === TAB_RATINGS && <RatingsManager />}
        {tab === TAB_ACTIVITY && <ActivityManager />}
        {tab === TAB_LLM_USAGE && <LlmUsageManager />}
        {tab === TAB_MCP_AUDIT && <McpAuditLog />}
        {tab === TAB_API_KEYS && <ApiKeyManager />}
        {tab === TAB_TESTING && <TestingManager />}
        {tab === TAB_TESTING_BRIEF && (
          <BriefTestingManager
            dataSource={dataSource}
            modelCombo={modelCombo}
            onResultClick={onResultClick}
          />
        )}
        {tab === TAB_TOC_VALIDATOR && (
          <TocValidatorManager
            dataSource={dataSource}
            dataSourceConfig={dataSourceConfig}
          />
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
